import React, { Component, type ComponentType } from 'react'

export interface InputDecoratorProps {
  errorId: string | boolean
  field?: { input: any; meta: any }
  value: any
  warningId: string | boolean
  onBlur?: (...args: any[]) => void
  onChange?: (...args: any[]) => void
  onClick?: (...args: any[]) => void
  onFocus?: (...args: any[]) => void
}

// `initialValue: any` — without the annotation TS infers `null` from the default and rejects `input('')`/`input([])` under strictNullChecks.
export default (initialValue: any = null, useState = true, { noFieldEvents = false } = {}) =>
  <P extends object>(DecoratedComponent: ComponentType<P>) => {
    return class InputDecorator extends Component<P & InputDecoratorProps> {
      static displayName = 'InputDecorator'
      state = {
        value: this.props.value || this.props.field?.input.value || initialValue,
      }

      componentWillReceiveProps({ value }: P & InputDecoratorProps) {
        if (this.props.value !== value) {
          this.setState({ value })
        }
      }

      getValue = () => (this.props.field ? this.props.field.input.value : this.state.value)

      handleChange = (...args: any[]) => {
        const { onChange, field } = this.props

        field?.input.onChange(...args)
        onChange?.(...args)
      }

      onBlur = (e: Event) => {
        const { onBlur, field } = this.props
        onBlur?.(e)
        field?.input?.onBlur?.(!noFieldEvents ? e : undefined)
      }

      onChange = (value: any, ...args: any[]) =>
        useState
          ? this.setState({ value }, this.handleChange(value, ...args) as any)
          : this.handleChange(value, ...args)

      onClick = (e: Event) => {
        const { onClick, field } = this.props
        onClick?.(e)
        field?.input?.onClick?.(e)
      }

      onFocus = (e: Event) => {
        const { onFocus, field } = this.props
        onFocus?.(e)
        field?.input?.onFocus?.(!noFieldEvents ? e : undefined)
      }

      render() {
        const { errorId, warningId, field, ...rest } = this.props

        const error = errorId || (field?.meta.touched && field.meta.error)
        const warning = warningId || (field?.meta.touched && field.meta.warning)

        return (
          <DecoratedComponent
            {...(rest as P)}
            errorId={error}
            onBlur={this.onBlur}
            onChange={this.onChange}
            onClick={this.onClick}
            onFocus={this.onFocus}
            value={this.getValue()}
            warningId={warning}
          />
        )
      }
    }
  }
